export type FileSelectionEntry = {
	file: File;
	path: string;
	isDirectory: false;
};

export type DirectorySelectionEntry = {
	path: string;
	isDirectory: true;
};

export type SelectionEntry = FileSelectionEntry | DirectorySelectionEntry;

export enum FolderTraversal {
	Shallow = 0,
	DeepWithoutDirectories = 1
}

export async function extractFileSelectionEntriesFromDataTransfer(
	dataTransfer: DataTransfer | null,
	traversal: FolderTraversal.Shallow
): Promise<SelectionEntry[]>;

export async function extractFileSelectionEntriesFromDataTransfer(
	dataTransfer: DataTransfer | null,
	traversal: FolderTraversal.DeepWithoutDirectories
): Promise<FileSelectionEntry[]>;

export async function extractFileSelectionEntriesFromDataTransfer(
	dataTransfer: DataTransfer | null,
	traversal: FolderTraversal
): Promise<SelectionEntry[]>;

export async function extractFileSelectionEntriesFromDataTransfer(
	dataTransfer: DataTransfer | null,
	traversal: FolderTraversal
): Promise<SelectionEntry[]> {
	const { items, files } = dataTransfer ?? {};

	const result: SelectionEntry[] = [];

	if (items) {
		const webkitEntries: FileSystemEntry[] = [];
		const fileEntries: FileSelectionEntry[] = [];

		for (let i = 0; i < items.length; i++) {
			const item: DataTransferItem = items[i];

			if (item.kind !== "file") {
				continue;
			}

			const fileSystemEntry: FileSystemEntry | null = item.webkitGetAsEntry();
			const file: File | null = item.getAsFile();

			if (fileSystemEntry) {
				webkitEntries.push(fileSystemEntry);
			} else {
				if (file) {
					fileEntries.push({
						file,
						path: file.name,
						isDirectory: false
					});
				}
			}
		}

		// We don't inline the extraction of the files in the above loop as calling .file() on a file system entry
		// will clear the DataTransferItemList and all items in it
		for (const entry of webkitEntries) {
			if (traversal === FolderTraversal.DeepWithoutDirectories) {
				const entriesInItem: FileSelectionEntry[] = await extractFileSelectionEntriesFromItem(
					entry,
					""
				);

				result.push(...entriesInItem);
			} else {
				const entryInItem: SelectionEntry = await extractSelectionEntryFromItem(entry);
				result.push(entryInItem);
			}
		}

		for (const entry of fileEntries) {
			result.push({...entry});
		}

	} else if (files) {
		result.push(
			...extractFileSelectionEntriesFromFileList(files)
				.map(x => ({...x, isDirectory: false}))
			);
	}
	return result;
}

export function extractFileSelectionEntriesFromFileList(list: FileList | null): FileSelectionEntry[] {
	const result: FileSelectionEntry[] = [];
	if (list) {
		for (let i = 0; i < list.length; i++) {
			const file: File = list[i];
			result.push({
				file,
				path: file.webkitRelativePath || file.name,
				isDirectory: false
			});
		}
	}

	return result;
}

async function extractFileSelectionEntriesFromItem(
	item: FileSystemEntry,
	path: string
): Promise<FileSelectionEntry[]> {
	if (item.isFile === true) {
		const entry: FileSelectionEntry = await extractFileSelectionEntriesFromFile(item as FileSystemFileEntry, path);
		return [entry];
	} else if (item.isDirectory === true) {
		return extractFileSelectionEntriesFromDirectory(item as FileSystemDirectoryEntry, path);
	} else {
		throw new Error(`Unknown file system entry type '${JSON.stringify(item)}'`);
	}
}

async function extractFileSelectionEntriesFromFile(
	item: FileSystemFileEntry,
	path: string
): Promise<FileSelectionEntry> {
	const file: File = await new Promise<File>(
		(resolve, reject) => item.file(resolve, reject)
	);

	return {
		file,
		path: path + item.name,
		isDirectory: false
	};
}

async function extractFileSelectionEntriesFromDirectory(
	item: FileSystemDirectoryEntry,
	path: string
): Promise<FileSelectionEntry[]> {
	const dirReader: FileSystemDirectoryReader = item.createReader();
	const results: FileSelectionEntry[] = [];
	while (true) {
		const entries = await new Promise<FileSystemEntry[]>(
			(resolve, reject) => dirReader.readEntries(resolve, reject)
		);

		if (entries.length === 0) {
			return results;
		}

		for (let i = 0; i < entries.length; i++) {
			const entriesInDirectory = await extractFileSelectionEntriesFromItem(entries[i], path + item.name + "/");
			results.push(...entriesInDirectory);
		}
	}
}

async function extractSelectionEntryFromItem(item: FileSystemEntry): Promise<SelectionEntry> {
	if (item.isDirectory && !item.isFile) {
		return {
			isDirectory: true,
			path: item.name
		};

	} else {
		const fileSelectionEntry: FileSelectionEntry = await extractFileSelectionEntriesFromFile(
			item as FileSystemFileEntry,
			""
		);

		return fileSelectionEntry;
	}
}